import type { FormEvent } from 'react';
import { useState } from 'react';
import { useElements } from '@stripe/react-stripe-js';
import type { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

import { AddressField } from '@/components/billing/address-field';
import { ExpiryInput } from '@/components/billing/expiry-input';
import { StripeElementsProvider } from '@/components/billing/stripe-elements-provider';
import { StandardAlert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Heading5 } from '@/components/ui/typography';
import { validateExpiry } from '@/lib/billing/payment-method-expiry-validation';
import http, { extractApiErrorMessage } from '@/lib/http';
import { cmsBillingPaymentMethodPath } from '@/lib/routes';
import type { UpdatePaymentMethodDialogProps } from '@/pages/cms/billing/types';

function mapStripeAddressToBillingAddress(
  stripeAddress: StripeAddressElementChangeEvent['value']['address'],
) {
  return {
    line1: stripeAddress.line1,
    line2: stripeAddress.line2 || '',
    city: stripeAddress.city,
    state: stripeAddress.state,
    postalCode: stripeAddress.postal_code,
    country: stripeAddress.country,
  };
}

function UpdatePaymentMethodForm({
  paymentMethod,
  googleMapsApiKey,
  onSuccess,
}: {
  paymentMethod: UpdatePaymentMethodDialogProps['paymentMethod'];
  googleMapsApiKey?: string;
  onSuccess: () => void;
}) {
  const elements = useElements();
  const cardLabel = `${paymentMethod.brand.toUpperCase()} •••• ${paymentMethod.last4}`;
  const [expMonth, setExpMonth] = useState(paymentMethod.expMonth);
  const [expYear, setExpYear] = useState(paymentMethod.expYear);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const expiryValidation = validateExpiry(expMonth, expYear);
    if (!expiryValidation.isValid) {
      setError(expiryValidation.error ?? 'Invalid expiration date');
      return;
    }

    if (!elements) {
      setError('Unable to process payment information. Please refresh the page and try again.');
      return;
    }

    const submitResult = await elements.submit();
    if (submitResult.error) {
      setError(submitResult.error.message || 'Please review your payment details.');
      return;
    }

    const addressElement = elements.getElement('address');
    const addressValue = addressElement
      ? ((await addressElement.getValue()) as StripeAddressElementChangeEvent)
      : null;
    if (!addressValue?.complete) {
      setError('Please complete the billing address.');
      return;
    }

    setProcessing(true);
    try {
      await http.patch(cmsBillingPaymentMethodPath(paymentMethod.id, { format: 'json' }), {
        paymentMethod: {
          paymentMethodId: paymentMethod.id,
          card: { expMonth, expYear },
          billingDetails: {
            name: addressValue.value.name,
            address: mapStripeAddressToBillingAddress(addressValue.value.address),
          },
        },
      });
      onSuccess();
    } catch (err) {
      setError(
        extractApiErrorMessage(err) ??
          (err instanceof Error ? err.message : 'Something went wrong. Please try again.'),
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Update Payment Method</DialogTitle>
        <DialogDescription className="space-y-2 text-sm">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{cardLabel}</span>
            {paymentMethod.default ? (
              <Badge size="sm" variant="outline" colorScheme="secondary">
                Default payment method
              </Badge>
            ) : null}
          </span>
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Heading5 className="mb-4">Payment Details</Heading5>
          <ExpiryInput
            label="Expiration date (MM/YYYY)"
            helperText="Update the expiry date for this card."
            initialMonth={paymentMethod.expMonth}
            initialYear={paymentMethod.expYear}
            disabled={processing}
            onChange={(month, year) => {
              setExpMonth(month);
              setExpYear(year);
            }}
          />
        </div>

        <div>
          <Heading5 className="mb-4">Billing Address</Heading5>
          <AddressField
            googleMapsApiKey={googleMapsApiKey}
            defaultValues={{
              name: paymentMethod.billingDetails?.name || '',
              address: {
                line1: paymentMethod.billingDetails?.address?.line1 || '',
                line2: paymentMethod.billingDetails?.address?.line2 || '',
                city: paymentMethod.billingDetails?.address?.city || '',
                state: paymentMethod.billingDetails?.address?.state || '',
                // biome-ignore lint/style/useNamingConvention: Stripe requires snake_case here
                postal_code: paymentMethod.billingDetails?.address?.postalCode || '',
                country: paymentMethod.billingDetails?.address?.country || '',
              },
            }}
          />
        </div>

        {error ? <StandardAlert variant="destructive" description={error} /> : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={processing}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" isLoading={processing}>
            {processing ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function UpdatePaymentMethodDialog({
  paymentMethod,
  stripePublicKey,
  googleMapsApiKey,
  open: controlledOpen,
  onOpenChange,
  triggerLabel = 'Update Card Details',
  onUpdated,
}: UpdatePaymentMethodDialogProps & {
  stripePublicKey: string;
  googleMapsApiKey?: string;
  /** Omit to let the dialog manage its own open state behind its own trigger button; pass both
   * to control it externally (e.g. from a dropdown menu's "Edit" item). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger text: "Update Card Details" on the billing page's own section, or "Edit" when
   * nested in a compact row (e.g. inside the reusable AddCreditsModal). Ignored when the dialog
   * is externally controlled via `open`/`onOpenChange`. */
  triggerLabel?: string;
  /** Called after the update succeeds and the dialog has closed, so the caller can refresh its
   * own data - this component has no opinion on that, since it's used in multiple places. */
  onUpdated?: () => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = onOpenChange !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isControlled ? null : (
        <DialogTrigger asChild>
          <Button variant="link">{triggerLabel}</Button>
        </DialogTrigger>
      )}
      <DialogContent size="md" dismissible={false}>
        {open ? (
          <StripeElementsProvider stripePublicKey={stripePublicKey}>
            <UpdatePaymentMethodForm
              paymentMethod={paymentMethod}
              googleMapsApiKey={googleMapsApiKey}
              onSuccess={() => {
                setOpen(false);
                onUpdated?.();
              }}
            />
          </StripeElementsProvider>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default UpdatePaymentMethodDialog;
